// V7: one bounded contract shared by OAuth, executor, V4 and the UI.
import { isExpansionReadValidationAccount } from './connectionValidation.ts';
export { isExpansionReadValidationAccount };
export const EXPANSION_PROVIDERS = ['dropbox', 'todoist', 'trello', 'slack', 'microsoft_teams'] as const;
export type ExpansionProvider = typeof EXPANSION_PROVIDERS[number];
export class ExpansionError extends Error {
  constructor(code: string, public status = 400) {
    super(code);
  }
}
export const isExpansionProvider = (value: unknown): value is ExpansionProvider =>
  EXPANSION_PROVIDERS.includes(value as ExpansionProvider);
export const expansionWrite = (action: string) => action === 'create' || action === 'send';
export const expansionEnabled = (provider: ExpansionProvider, env: (key: string) => string | undefined) =>
  env('EXPANSION_EXECUTOR_ENABLED') === 'true' &&
  env('EXPANSION_' + provider.toUpperCase() + '_ENABLED') === 'true';
export const expansionConfig = {
  dropbox: {
    client: 'DROPBOX_CLIENT_ID',
    secret: 'DROPBOX_CLIENT_SECRET',
    authorize: 'https://www.dropbox.com/oauth2/authorize',
    token: 'https://api.dropboxapi.com/oauth2/token',
    scopes: ['account_info.read', 'files.metadata.read', 'files.content.read'],
    write: 'files.content.write',
    pkce: true,
  },
  todoist: {
    client: 'TODOIST_CLIENT_ID',
    secret: 'TODOIST_CLIENT_SECRET',
    authorize: 'https://app.todoist.com/oauth/authorize',
    token: 'https://api.todoist.com/oauth/access_token',
    scopes: ['data:read'],
    write: 'task:add',
    pkce: true,
  },
  trello: {
    client: 'TRELLO_CLIENT_ID',
    secret: 'TRELLO_CLIENT_SECRET',
    authorize: 'https://auth.atlassian.com/authorize',
    token: 'https://auth.atlassian.com/oauth/token',
    scopes: ['read:member:trello', 'read:board:trello', 'offline_access'],
    write: 'write:board:trello',
    pkce: true,
  },
  slack: {
    client: 'SLACK_CLIENT_ID',
    secret: 'SLACK_CLIENT_SECRET',
    authorize: 'https://slack.com/oauth/v2/authorize',
    token: 'https://slack.com/api/oauth.v2.access',
    scopes: ['channels:read', 'groups:read', 'channels:history', 'groups:history'],
    write: 'chat:write',
    pkce: false,
  },
  microsoft_teams: {
    client: 'OUTLOOK_OAUTH_CLIENT_ID',
    secret: 'OUTLOOK_OAUTH_CLIENT_SECRET',
    authorize: 'https://login.microsoftonline.com/organizations/oauth2/v2.0/authorize',
    token: 'https://login.microsoftonline.com/organizations/oauth2/v2.0/token',
    scopes: ['User.Read', 'Chat.Read', 'offline_access'],
    write: 'ChatMessage.Send',
    pkce: true,
  },
} as const;
export type ExpansionAccess = 'read' | 'create' | 'edit';
/** Destructive consent is a separate explicit path; legacy access parsing stays unchanged. */
export type ExpansionDeleteAccess = 'delete';
/** Legacy write=true requests creation only. Existing grants are never upgraded implicitly. */
export function expansionAccess(value: unknown): ExpansionAccess {
  if (value === false || value === undefined) return 'read';
  if (value === true) return 'create';
  if (['read', 'create', 'edit'].includes(value as string)) return value as ExpansionAccess;
  throw new ExpansionError('unsupported_access');
}
export function expansionScopes(provider: ExpansionProvider, value: boolean | ExpansionAccess | ExpansionDeleteAccess) {
  if (value === 'delete') {
    if (provider === 'todoist') return ['data:read', 'data:delete'];
    if (provider === 'trello') return [...expansionConfig.trello.scopes, expansionConfig.trello.write];
    throw new ExpansionError('unsupported_access');
  }
  const access = expansionAccess(value);
  if (access === 'edit' && !['todoist', 'trello'].includes(provider)) {
    throw new ExpansionError('unsupported_access');
  }
  if (provider === 'todoist' && access === 'edit') return ['data:read_write'];
  return [...expansionConfig[provider].scopes,
    ...(access !== 'read' ? [expansionConfig[provider].write] : [])];
}
/** Todoist documents read_write as read + task:add, never as delete. */
function expansionScopeGranted(provider: ExpansionProvider, scopes: string[], required: string) {
  const scope = required.toLowerCase();
  return scope === 'offline_access' || scopes.includes(scope) ||
    provider === 'todoist' && ['data:read', 'task:add'].includes(scope) &&
      scopes.includes('data:read_write');
}
export function hasExpansionScopes(
  provider: ExpansionProvider, granted: unknown, access: boolean | ExpansionAccess | ExpansionDeleteAccess,
) {
  if (!Array.isArray(granted) || granted.some(s => typeof s !== 'string' || !s.trim())) return false;
  const scopes = granted.map(s => s.toLowerCase());
  return expansionScopes(provider, access).every(s => expansionScopeGranted(provider, scopes, s));
}
/** Validate the consumed OAuth state's exact request, including edit grants. */
export function hasRequestedExpansionScopes(provider: ExpansionProvider, granted: unknown, requested: unknown, allowTaskDelete = false) {
  if (!Array.isArray(granted) || !Array.isArray(requested) || !requested.length ||
    [...granted, ...requested].some(s => typeof s !== 'string' || !s.trim())) return false;
  const allowed = [...expansionConfig[provider].scopes, expansionConfig[provider].write,
    ...(provider === 'todoist' ? ['data:read_write', ...(allowTaskDelete ? ['data:delete'] : [])] : [])].map(s => s.toLowerCase());
  const scopes = granted.map(s => s.toLowerCase());
  return requested.every(s => allowed.includes(s.toLowerCase()) &&
    expansionScopeGranted(provider, scopes, s));
}
/** Only an explicit deletion upgrade carries forward known existing grants. */
export function expansionDeleteConsentScopes(provider: ExpansionProvider, current: unknown) {
  const required = expansionScopes(provider, 'delete');
  if (current === null || current === undefined) return required;
  if (!Array.isArray(current) || current.some(s => typeof s !== 'string' || !s.trim())) {
    throw new ExpansionError('permissions_incomplete', 403);
  }
  const allowed = [...expansionConfig[provider].scopes, expansionConfig[provider].write,
    ...(provider === 'todoist' ? ['data:read_write', 'data:delete'] : [])];
  const retained = current.map(s => s.toLowerCase()).filter(s => allowed.includes(s));
  return [...new Set([...retained, ...required])];
}
export interface ExpansionRequest {
  provider: ExpansionProvider;
  action: 'probe' | 'lists' | 'search' | 'get' | 'create' | 'send';
  list_id?: string;
  item_id?: string;
  query?: string;
  cursor?: string;
  title?: string;
  notes?: string;
  due_date?: string;
  name?: string;
  content_text?: string;
  body_text?: string;
  account_name?: string;
  account_id?: string;
  destination_name?: string;
  confirmed?: true;
  idempotency_key?: string;
}
const limits: Record<string, number> = {
  account_name: 160,
  account_id: 200,
  destination_name: 200,
  list_id: 1000,
  item_id: 1000,
  query: 200,
  cursor: 8000,
  title: 200,
  notes: 4000,
  due_date: 10,
  name: 180,
  content_text: 20000,
  body_text: 4000,
};
export function validateExpansionRequest(raw: unknown): ExpansionRequest {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new ExpansionError('invalid_request');
  const b = raw as Record<string, unknown>;
  if (!isExpansionProvider(b.provider)) throw new ExpansionError('unsupported_provider');
  const action = String(b.action), messaging = ['slack', 'microsoft_teams'].includes(b.provider);
  if (!['probe', 'lists', 'search', 'get', messaging ? 'send' : 'create'].includes(action)) {
    throw new ExpansionError('unsupported_action');
  }
  const out: Record<string, unknown> = { provider: b.provider, action };
  for (const [key, max] of Object.entries(limits)) {
    if (b[key] !== undefined && b[key] !== null && b[key] !== '') {
      if (
        typeof b[key] !== 'string' || b[key].length > max ||
        /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(b[key] as string)
      ) throw new ExpansionError('invalid_' + key);
      out[key] = ['content_text', 'body_text', 'notes'].includes(key) ? b[key] : (b[key] as string).trim();
    }
  }
  const required = (key: string) => {
    if (typeof out[key] !== 'string' || !(out[key] as string).trim()) {
      throw new ExpansionError(key + '_required');
    }
  };
  if (action === 'get') required('item_id');
  if (
    (messaging && ['get', 'search', 'send'].includes(action)) ||
    (b.provider === 'trello' && ['search', 'create'].includes(action)) ||
    (b.provider === 'todoist' && action === 'create')
  ) required('list_id');
  if (action === 'create') {
    if (b.provider === 'dropbox') {
      required('name');
      required('content_text');
      if (
        /[/\\:*?"<>|\r\n]/.test(String(out.name)) || /^\.+$/.test(String(out.name)) ||
        !/\.(txt|md|csv)$/i.test(String(out.name))
      ) throw new ExpansionError('text_filename_required');
    } else required('title');
  }
  if (action === 'send') required('body_text');
  if (
    out.due_date &&
    (!/^\d{4}-\d{2}-\d{2}$/.test(String(out.due_date)) ||
      !Number.isFinite(Date.parse(String(out.due_date))) ||
      new Date(String(out.due_date)).toISOString().slice(0, 10) !== out.due_date)
  ) throw new ExpansionError('invalid_due_date');
  if (b.provider !== 'dropbox') {
    for (const field of ['list_id', 'item_id']) {
      if (out[field] && !/^[A-Za-z0-9_:.@-]+$/.test(String(out[field]))) {
        throw new ExpansionError('invalid_' + field);
      }
    }
  }
  if (b.provider === 'dropbox') {
    for (const field of ['list_id', 'item_id']) {
      if (
        out[field] &&
        (!/^(\/|id:)/.test(String(out[field])) || /(^|\/)\.\.?($|\/)|[\r\n\\]/.test(String(out[field])))
      ) throw new ExpansionError('invalid_' + field);
    }
  }
  if (
    b.provider === 'dropbox' && action === 'create' && out.list_id && !String(out.list_id).startsWith('/')
  ) throw new ExpansionError('invalid_list_id');
  if (expansionWrite(action)) {
    required('account_id');
    if (out.list_id) required('destination_name');
    if (b.confirmed !== true) throw new ExpansionError('approval_required', 403);
    if (typeof b.idempotency_key !== 'string' || !/^[\w:-]{1,180}$/.test(b.idempotency_key)) {
      throw new ExpansionError('invalid_action_id');
    }
    out.confirmed = true;
    out.idempotency_key = b.idempotency_key;
  }
  return out as unknown as ExpansionRequest;
}
export interface ExpansionItem {
  id: string;
  title: string;
  kind?: string;
  list_id?: string;
  notes?: string;
  content_text?: string;
  body_text?: string;
  due_date?: string;
  path?: string;
  from?: string;
  webUrl?: string;
  truncated?: boolean;
}
export const textValue = (v: unknown, max = 2000) =>
  typeof v === 'string' ? v.replace(/\u0000/g, '').slice(0, max) : '';
export const plainText = (v: unknown) =>
  textValue(v, 40000).replace(/<[^>]*>/g, ' ').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(
    /&amp;/g,
    '&',
  ).replace(/&quot;/g, '"').trim().slice(0, 20000);
